import type { ComponentPropsWithoutRef } from "react";
import { tv } from "tailwind-variants";
import { t } from "ttag";

import { usePortalUserStore } from "@/app/stores";
import {
  ActiveSubscriptionCard,
  SubscriptionsSkeleton,
} from "@/features/billing/components";
import { useActiveSubscriptions } from "@/features/billing/hooks";
import { QueuedCreditAlert } from "@/features/referral/components";
import { Card, CheckCircleIcon } from "@/shared/ui";

const activeSubscriptionsStyles = tv({
  slots: {
    root: "gap-2",
    header: "flex-col gap-4",
    subscriptionLists: "flex flex-col gap-2",
  },
});

const { root, header, subscriptionLists } = activeSubscriptionsStyles();

export const ActiveSubscriptions = ({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Card.Root>) => {
  const {
    data: activeSubscriptions,
    isLoading,
    isSuccess,
  } = useActiveSubscriptions();

  const emptySubscriptions = usePortalUserStore(
    (state) => !state.portalUser?.subscriptions.length,
  );

  if (isSuccess && emptySubscriptions) {
    return null;
  }

  return (
    <Card.Root color="secondary" className={root({ className })} {...props}>
      <Card.Header className={header()}>
        <Card.Title icon={CheckCircleIcon}>{t`Your subscriptions`}</Card.Title>

        <QueuedCreditAlert />
      </Card.Header>

      <Card.Content>
        {isLoading && <SubscriptionsSkeleton />}

        {isSuccess && (
          <div className={subscriptionLists()}>
            {activeSubscriptions.map((subscription) => (
              <ActiveSubscriptionCard
                key={subscription.id}
                subscription={subscription}
              />
            ))}
          </div>
        )}
      </Card.Content>
    </Card.Root>
  );
};
